import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FilePlus,
  Folder,
  FolderOpen,
  FileText,
  User,
  LogOut,
  Calendar,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  CalendarDays,
  BookOpen,
} from "lucide-react";
import "./Sidebar.css";

const Sidebar = ({ isOpen, closeSidebar }) => {
  const location = useLocation();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Sub-paths for Projects group
  const projectSubPaths = ["/projects", "/project-module", "/my-projects"];
  const isProjectGroupActive = projectSubPaths.some(
    (p) => location.pathname === p,
  );
  const [projectsOpen, setProjectsOpen] = useState(isProjectGroupActive);

  // Sub-paths for Reports group
  const reportSubPaths = [
    "/add-workreports",
    "/my-reports",
    "/additional-work-report",
  ];
  const isReportGroupActive = reportSubPaths.some(
    (p) => location.pathname === p,
  );
  const [reportsOpen, setReportsOpen] = useState(isReportGroupActive);

  const handleLogoutClick = (e) => {
    e.preventDefault();
    setShowLogoutDialog(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem("employeeToken");
    localStorage.clear();
    window.location.href = "/";
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  const handleLinkClick = () => {
    if (closeSidebar) closeSidebar();
  };

  const handleMainLinkClick = () => {
    setProjectsOpen(false);
    setReportsOpen(false);
    if (closeSidebar) closeSidebar();
  };

  const toggleProjects = () => {
    setProjectsOpen((prev) => !prev);
    setReportsOpen(false);
  };

  const toggleReports = () => {
    setReportsOpen((prev) => !prev);
    setProjectsOpen(false);
  };

  // Flat nav items
  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/attendance", label: "Attendance", icon: CalendarDays },
    { path: "/leave", label: "Leave", icon: Calendar },
    { path: "/tasks", label: "Tasks", icon: ClipboardList },
    { path: "/holiday", label: "Holidays", icon: CalendarDays },
    { path: "/policy", label: "Policy", icon: BookOpen },
    { path: "/profile", label: "Profile", icon: User },
  ];

  const projectSubItems = [
    { path: "/projects", label: "Projects", icon: FolderKanban },
    { path: "/my-projects", label: "My Projects", icon: Folder },
  ];

  const reportSubItems = [
    { path: "/add-workreports", label: "Add Workreport", icon: FilePlus },
    { path: "/my-reports", label: "My Reports", icon: FileText },
    {
      path: "/additional-work-report",
      label: "Additional Work Report",
      icon: ClipboardList,
    },
  ];

  return (
    <div className={`sidebar ${isOpen ? "open" : ""}`}>
      {/* Logo */}
      <div className="sidebar-header-wrapper">
        <div className="sidebar-logo-container">
          <img
            src="https://www.qpayindia.com/img/qpaylogo.png"
            alt="QPay India Logo"
            height={60}
          />
        </div>
      </div>

      <nav className="sidebar-nav">
        <ul>
          {/* Dashboard */}
          <li className={location.pathname === "/" ? "active" : ""}>
            <Link to="/" onClick={handleMainLinkClick}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </Link>
          </li>

          {/* ---- Projects expandable group ---- */}
          <li
            className={`nav-group ${isProjectGroupActive ? "group-active" : ""}`}
          >
            <button
              className={`nav-group-btn ${projectsOpen ? "open" : ""}`}
              onClick={toggleProjects}
              aria-expanded={projectsOpen}
            >
              <span className="nav-group-btn-left">
                {projectsOpen ? <FolderOpen size={20} /> : <Folder size={20} />}
                <span>Projects</span>
              </span>
              <span className="nav-group-chevron">
                {projectsOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </span>
            </button>
            <ul className={`nav-submenu ${projectsOpen ? "submenu-open" : ""}`}>
              {projectSubItems.map((sub) => {
                const SubIcon = sub.icon;
                const isActive = location.pathname === sub.path;
                return (
                  <li key={sub.path} className={isActive ? "active" : ""}>
                    <Link to={sub.path} onClick={handleLinkClick}>
                      <SubIcon size={17} />
                      <span>{sub.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* ---- Reports expandable group ---- */}
          <li
            className={`nav-group ${isReportGroupActive ? "group-active" : ""}`}
          >
            <button
              className={`nav-group-btn ${reportsOpen ? "open" : ""}`}
              onClick={toggleReports}
              aria-expanded={reportsOpen}
            >
              <span className="nav-group-btn-left">
                <ClipboardList size={20} />
                <span>Reports</span>
              </span>
              <span className="nav-group-chevron">
                {reportsOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </span>
            </button>
            <ul className={`nav-submenu ${reportsOpen ? "submenu-open" : ""}`}>
              {reportSubItems.map((sub) => {
                const SubIcon = sub.icon;
                const isActive = location.pathname === sub.path;
                return (
                  <li key={sub.path} className={isActive ? "active" : ""}>
                    <Link to={sub.path} onClick={handleLinkClick}>
                      <SubIcon size={17} />
                      <span>{sub.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {navItems.slice(1).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path} className={isActive ? "active" : ""}>
                <Link to={item.path} onClick={handleMainLinkClick}>
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="logout-item">
        <a href="/" onClick={handleLogoutClick}>
          <LogOut size={20} />
          <span>Logout</span>
        </a>
      </div>

      {/* Logout Confirm Modal */}
      {showLogoutDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(3px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "32px 36px",
              borderRadius: "16px",
              minWidth: "360px",
              maxWidth: "90%",
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              textAlign: "center",
              fontFamily: "Inter, sans-serif",
              animation: "fadeInScale 0.2s ease",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "22px",
              }}
            >
              🔒
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                color: "#0f172a",
                fontSize: "18px",
                fontWeight: "700",
              }}
            >
              Confirm Logout
            </h3>
            <p
              style={{
                color: "#64748b",
                margin: "0 0 28px 0",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to log out of your account?
            </p>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "12px" }}
            >
              <button
                onClick={cancelLogout}
                style={{
                  padding: "10px 24px",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "8px",
                  background: "#ffffff",
                  color: "#334155",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontFamily: "inherit",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f8fafc";
                  e.target.style.borderColor = "#94a3b8";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#ffffff";
                  e.target.style.borderColor = "#e2e8f0";
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontFamily: "inherit",
                }}
                onMouseOver={(e) => (e.target.style.opacity = "0.85")}
                onMouseOut={(e) => (e.target.style.opacity = "1")}
              >
                Logout
              </button>
            </div>
            <style>{`@keyframes fadeInScale { from { opacity:0; transform: scale(0.93); } to { opacity:1; transform: scale(1); } }`}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
